package com.neurobs.util

import com.neurobs.ccab.{ChatClientId}
import com.neurobs.js.dom.VideoElement
import com.neurobs.ccab.CaasType

/** The current state of AV transmission
  *
  * @param remoteAudio The remote system is sending audio
  * @param remoteVideo The remote system is sending video
  * @param localAudio Audio is being sent to the remote system
  * @param localVideo Video is being sent to the remote system
  */
case class CommunicationState
(
	remoteAudio: Boolean = false,
	remoteVideo: Boolean = false,
	localAudio: Boolean = false,
	localVideo: Boolean = false
) {
	def localAV: Boolean = localAudio || localVideo
}

/** Describes the connection of the client to a session, not
  * necessarily any other clients in the same session. */
object ChatConnectionStatus extends Enumeration {
	type Status = Value
	/** The client is attempting to connect for the first time */
	val Connecting = Value
	/** The client is connected to the session */
	val Connected = Value
	/** The client is not connected, but the connection may return */
	val Disconnected = Value
	/** The client is not connected, and will never be reconnected */
	val Closed = Value
}

trait ChatSessionNotify {
	/** Called when the connection status changes
	  *
	  * @param newStatus New status
	  * @param error Error message if available
	  */
	def connectionStatusChanged(newStatus: ChatConnectionStatus.Value, error: Option[String]): Unit

	/** Called when the communication state changes
	  *
	  * @param newState The new state
	  */
	def communicationStateChanged(newState: CommunicationState): Unit

	/** Called when there is an error starting AV transmission
	  * to other clients
	  */
	def publishAVError(error: String): Unit

	/** Called when there is an error subscribing to a remote stream
	  */
	def subscribeToRemoteError(error: String): Unit

	/** Called  for other errors */
	def genericError(error: String): Unit

	/** Called when another client connects to the session
	  *
	  * @param id Data identifying the client connecting
	  */
	def remoteClientConnected(id: ChatClientId): Unit

	/** Called when another client disconnects from the session
	  *
	  * @param id Data identifying the client disconnecting
	  */
	def remoteClientDisconnected(id: ChatClientId): Unit

	/** Called when a text message is received
	  *
	  * @param message The text of the message
	  * @param messageType The type of message, as specified by the source
	  * @param source String identifying the source of the message, usually CommConnectionId.user
	  */
	def messageReceived(message: String, messageType: String, source: String): Unit
}

/** Implements a connection to a specific chat service.
  *
  * The text messaging aspect is abstracted from the specific Test Station/Examiner
  * use case. However, the A/V streams which implementations handle is not. Implementations
  * must handle A/V streams in the following way:
  * 1. It must distinguish between A/V streams coming from Test Station or Examiner
  * 2. Any A/V streams from Test Station should be automatically played (in remoteVideoElement)
  * 3. Any A/V streams from another Examiner should be automatically played
  *    (in remoteAdminVideoElement), and if this client is publishing any streams, it
  *    should stop.
  *
  */
trait ChatSession {
	def apiType: CaasType.Value

	/** The connection status of this client to the session (not to other clients.)  */
	def connectionStatus: ChatConnectionStatus.Value

	/** Sends a text message to the session from this client.
	  *
	  * @param message The message
	  * @param messageType A caller defined message classification that should
	  *                    be sent as meta-data along with the message.
	  */
	def sendMessage(message: String, messageType: String): Unit

	/** Indicates if this client is currently sending any A/V streams to the session. */
	def isPublishingAV: Boolean

	/** Sets the status of A/V streams being sent from this client
	  *
	  * @param audio If true, an audio stream from the default audio source
	  *              is sent to the session.
	  * @param video If true, a video stream from the default video source
	  *              is sent to the session.
	  */
	def setPublishing(audio: Boolean, video: Boolean): Unit

	/** Closes the connection of this client to the session. */
	def close(): Unit

	/** Sets the volume level of any foreign audio streams being played
	  * on this system. The level should be preserved between audio streams.
	  *
	  * @param volume Level in the range [0,100]
	  */
	def setPlaybackVolume(volume: Double): Unit
}

/** Creates a new ChatSession object.
  *
  * Implementations of this will typically just store connection parameters
  * or other configuration necessary to connect to a particular chat service.
  */
trait ChatSessionFactory {
	/** Starts the connection process to a Chat session.
	  *
	  * @param thisClient Data describing this client
	  * @param notify Callback interface
	  * @param localVideoElement HTML element in which to display any video stream
	  *                          this client sends to the session (so the user can
	  *                          see themselves.)
	  * @param remoteVideoElement HTML element in which to display video streams
	  *                           received from Test Station
	  * @param remoteAdminVideoElement HTML element in which to display video streams
	  *                                received from other Examiner instances
	  * @return An error message if there is an immediate error, or a ChatSession object.
	  *         If the connection attempt later fails, report the error by calling
	  *         ChatSessionNotify.connectionStatusChanged.
	  */
	def connect(thisClient: ChatClientId, notify: ChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement): Either[String, ChatSession]
}
